import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import octopusLogo from "@/assets/octopus-logo-official.png";
import { Menu, X } from "lucide-react";
import SiteSearch from "@/components/SiteSearch";

const navLinks = [
  { label: "How It Works", href: "#how-it-works" },
  { label: "The Organism", href: "#organism" },
  { label: "Stories", href: "#evidence" },
  { label: "Alive Now", href: "#alive-now" },
  { label: "Who Holds the Space", href: "#who-holds-the-space" },
  { label: "Find Your Way In", href: "#find-your-way-in" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleAnchorClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    setIsOpen(false);
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
      <div className="container mx-auto flex items-center justify-between h-16 px-4">
        <Link to="/" className="flex items-center gap-3">
          <img src={octopusLogo} alt="TOM logo" className="h-10 w-10" />
          <span className="font-heading font-bold text-lg text-foreground">the Octopus Movement</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-4 lg:gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className="text-xs lg:text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer whitespace-nowrap"
            >
              {link.label}
            </a>
          ))}
          <SiteSearch />
          <Button variant="coral" size="sm" asChild>
            <a href="https://airtable.com/appj9naWTSEyBxyub/shrahOKBrQHWbDb65" target="_blank" rel="noopener noreferrer">
              Join
            </a>
          </Button>
        </div>

        {/* Mobile toggle */}
      <button className="md:hidden text-foreground" onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? "Close menu" : "Open menu"}>
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border pb-4 px-4">
          <div className="pt-3">
            <SiteSearch variant="full" />
          </div>
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => handleAnchorClick(e, link.href)}
              className="block py-3 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              {link.label}
            </a>
          ))}
          <Button variant="coral" size="sm" className="mt-2 w-full" asChild>
            <a href="https://airtable.com/appj9naWTSEyBxyub/shrahOKBrQHWbDb65" target="_blank" rel="noopener noreferrer">
              Join the Movement
            </a>
          </Button>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
